import { resolveStableAnimationDelaySec } from "@repo/utils";

export const HEXAGON_MAP_MIN_USER_ZOOM = 1;
export const HEXAGON_MAP_MAX_USER_ZOOM = 3;

/** Discrete zoom step for +/- buttons and keyboard shortcuts. */
export const HEXAGON_MAP_ZOOM_BUTTON_STEP = 0.25;

/** Fine step for the zoom slider so dragging feels continuous. */
export const HEXAGON_MAP_ZOOM_SLIDER_STEP = 0.01;

/** Gap above anchor at min user zoom (fully zoomed out), in content px. */
export const HEXAGON_MAP_OVERLAY_GAP_ABOVE_ANCHOR_MAX_PX = 8;
/** Gap above anchor at max user zoom (fully zoomed in), in content px. */
export const HEXAGON_MAP_OVERLAY_GAP_ABOVE_ANCHOR_MIN_PX = 0;

/** Player pin gap above character top when fully zoomed out (tighter than speech bubbles). */
export const HEXAGON_MAP_PLAYER_PIN_GAP_ABOVE_CHARACTER_MAX_PX = -14;
/** Player pin gap above character top when fully zoomed in. */
export const HEXAGON_MAP_PLAYER_PIN_GAP_ABOVE_CHARACTER_MIN_PX = -4;

/** Slower idle float for map portrait bubbles and player pin (seconds). */
export const HEXAGON_MAP_OVERLAY_FLOAT_DURATION_S = 2.5;

/** Stable per-stop phase offset so floating bubbles are not synchronized. */
export function resolveMapSpeechBubbleFloatDelaySec(stopKey: string): number {
	return resolveStableAnimationDelaySec(
		`${stopKey}:map-speech-float-delay`,
		HEXAGON_MAP_OVERLAY_FLOAT_DURATION_S,
	);
}

/**
 * Vertical gap between map overlay anchor (tile top or character top) and overlay bottom edge.
 * Shrinks as the user zooms in — same curve as stop speech bubbles.
 */
export function resolveMapOverlayGapAboveAnchorPx(userZoom: number): number {
	const span = HEXAGON_MAP_MAX_USER_ZOOM - HEXAGON_MAP_MIN_USER_ZOOM;
	if (span <= 0) {
		return HEXAGON_MAP_OVERLAY_GAP_ABOVE_ANCHOR_MAX_PX;
	}
	const t = Math.min(
		1,
		Math.max(0, (userZoom - HEXAGON_MAP_MIN_USER_ZOOM) / span),
	);
	return (
		HEXAGON_MAP_OVERLAY_GAP_ABOVE_ANCHOR_MAX_PX +
		t *
			(HEXAGON_MAP_OVERLAY_GAP_ABOVE_ANCHOR_MIN_PX -
				HEXAGON_MAP_OVERLAY_GAP_ABOVE_ANCHOR_MAX_PX)
	);
}

/** Vertical gap between character top and player pin bottom (zoom-scaled). */
export function resolveMapPlayerPinGapAboveCharacterPx(
	userZoom: number,
): number {
	const span = HEXAGON_MAP_MAX_USER_ZOOM - HEXAGON_MAP_MIN_USER_ZOOM;
	if (span <= 0) {
		return HEXAGON_MAP_PLAYER_PIN_GAP_ABOVE_CHARACTER_MAX_PX;
	}
	const t = Math.min(
		1,
		Math.max(0, (userZoom - HEXAGON_MAP_MIN_USER_ZOOM) / span),
	);
	return (
		HEXAGON_MAP_PLAYER_PIN_GAP_ABOVE_CHARACTER_MAX_PX +
		t *
			(HEXAGON_MAP_PLAYER_PIN_GAP_ABOVE_CHARACTER_MIN_PX -
				HEXAGON_MAP_PLAYER_PIN_GAP_ABOVE_CHARACTER_MAX_PX)
	);
}
